/**
 * NotificationBell — top-right bell with unread count + dropdown inbox.
 *
 * Wired to the unified Notification Engine (Phase 1):
 *   GET  /api/notify/unread-count
 *   GET  /api/notify/inbox
 *   POST /api/notify/:id/read
 *   POST /api/notify/mark-all-read
 *
 * Polls every 20s.  Pops a live sonner toast for any assignment.added /
 * assignment.removed event that arrives between polls, so an added or
 * removed clinician sees the change within seconds of the admin saving.
 * Closes on outside-click.  Deep-link aware.
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Bell } from 'lucide-react';
import axios from 'axios';
import { toast as sonner } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const POLL_MS = 20000;
// Notification event types that surface as an in-page toast on arrival
const TOAST_EVENT_TYPES = new Set([
  'assignment.added',
  'assignment.removed',
]);

const SEVERITY_STYLES = {
  P0: 'bg-red-100 text-red-800 border-red-200',
  P1: 'bg-amber-100 text-amber-800 border-amber-200',
  P2: 'bg-blue-100 text-blue-800 border-blue-200',
  P3: 'bg-slate-100 text-slate-800 border-slate-200',
};

const NotificationBell = ({ token }) => {
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [rateLimit, setRateLimit] = useState(null); // { P0: {cap,used,remaining,throttled}, P1: {...} }
  const ref = useRef(null);
  // Track notification IDs we've already toasted so we never fire the same
  // toast twice across polls.  `seenReady` starts false so the FIRST poll
  // silently primes the set (avoids toasting every unread on refresh).
  const seenIdsRef = useRef(new Set());
  const seenReadyRef = useRef(false);

  const fetchUnread = useCallback(async () => {
    if (!token) return;
    try {
      const r = await axios.get(`${API_URL}/api/notify/unread-count`,
        { headers: { Authorization: `Bearer ${token}` } });
      setUnread(r.data?.count || 0);
    } catch { /* silent */ }
  }, [token]);

  // Lightweight recent-inbox poll used to detect brand-new events that
  // deserve a live toast.  Uses limit=8 so payload stays tiny.
  const pollForToasts = useCallback(async () => {
    if (!token) return;
    try {
      const r = await axios.get(`${API_URL}/api/notify/inbox?limit=8`,
        { headers: { Authorization: `Bearer ${token}` } });
      setUnread(r.data?.unread || 0);
      const rows = r.data?.data || [];
      if (!seenReadyRef.current) {
        // First poll — prime the "seen" set so we don't toast existing items
        rows.forEach(n => seenIdsRef.current.add(n.id));
        seenReadyRef.current = true;
        return;
      }
      rows.forEach(n => {
        if (seenIdsRef.current.has(n.id)) return;
        seenIdsRef.current.add(n.id);
        // Only toast if this is an event we've flagged as "live-worthy"
        // AND it's still unread (belongs to me).
        const stillUnread = !(n.read_by || []).includes('self');
        if (stillUnread && TOAST_EVENT_TYPES.has(n.event_type)) {
          sonner(n.title || 'Assignment updated', {
            description: (n.body_md || '').split('\n')[0].slice(0, 180),
            duration: 8000,
            action: n.deep_link ? {
              label: 'Open',
              onClick: () => { window.location.href = n.deep_link; },
            } : undefined,
          });
        }
      });
    } catch { /* silent */ }
  }, [token]);

  const fetchInbox = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const r = await axios.get(`${API_URL}/api/notify/inbox?limit=25`,
        { headers: { Authorization: `Bearer ${token}` } });
      setItems(r.data?.data || []);
      setUnread(r.data?.unread || 0);
    } catch { /* silent */ } finally { setLoading(false); }
  }, [token]);

  const fetchRateLimit = useCallback(async () => {
    if (!token) return;
    try {
      const r = await axios.get(`${API_URL}/api/notify/me/rate-limit-status`,
        { headers: { Authorization: `Bearer ${token}` } });
      setRateLimit(r.data?.by_severity || null);
    } catch { /* silent */ }
  }, [token]);

  // Poll unread + toast-eligible items every POLL_MS
  useEffect(() => {
    fetchUnread();
    pollForToasts();
    const t = setInterval(pollForToasts, POLL_MS);
    return () => clearInterval(t);
  }, [fetchUnread, pollForToasts]);

  // Refresh inbox + rate-limit when dropdown opens
  useEffect(() => {
    if (open) {
      fetchInbox();
      fetchRateLimit();
    }
  }, [open, fetchInbox, fetchRateLimit]);

  // Outside click closes dropdown
  useEffect(() => {
    if (!open) return;
    const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [open]);

  const markRead = async (nid) => {
    try {
      await axios.post(`${API_URL}/api/notify/${nid}/read`, {},
        { headers: { Authorization: `Bearer ${token}` } });
      setItems(prev => prev.map(n => n.id === nid ? { ...n, read_by: [...(n.read_by || []), 'self'] } : n));
      setUnread(u => Math.max(0, u - 1));
    } catch { /* silent */ }
  };

  const markAllRead = async () => {
    try {
      await axios.post(`${API_URL}/api/notify/mark-all-read`, {},
        { headers: { Authorization: `Bearer ${token}` } });
      setUnread(0);
      fetchInbox();
    } catch { /* silent */ }
  };

  const goDeepLink = (n) => {
    markRead(n.id);
    if (n.deep_link) window.location.href = n.deep_link;
    setOpen(false);
  };

  return (
    <div ref={ref} className="relative inline-block" data-testid="notification-bell">
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="relative p-2 rounded-full hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-cyan-500"
        aria-label={`Notifications, ${unread} unread`}
        data-testid="notification-bell-trigger"
      >
        <Bell className="w-5 h-5 text-gray-700" />
        {unread > 0 && (
          <span
            className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-red-600 text-white text-[10px] font-bold flex items-center justify-center"
            data-testid="notification-bell-badge"
          >
            {unread > 99 ? '99+' : unread}
          </span>
        )}
      </button>

      {open && (
        <div
          className="absolute right-0 mt-2 w-96 max-w-[90vw] bg-white rounded-lg shadow-xl border z-50 max-h-[70vh] overflow-hidden flex flex-col"
          data-testid="notification-dropdown"
        >
          <div className="flex items-center justify-between px-4 py-3 border-b bg-gradient-to-r from-cyan-50 to-blue-50">
            <h3 className="font-semibold text-gray-800">Notifications</h3>
            {unread > 0 && (
              <button
                onClick={markAllRead}
                className="text-xs text-cyan-700 hover:underline"
                data-testid="notification-mark-all-read"
              >
                Mark all read
              </button>
            )}
          </div>

          {/* Rate-limit banner — only shown when user is near or at the cap. */}
          {rateLimit && Object.entries(rateLimit).some(([, b]) => b.throttled || b.remaining <= 2) && (
            <div
              className="px-4 py-2 bg-amber-50 border-b border-amber-200 text-xs text-amber-900 flex items-center gap-2 flex-wrap"
              data-testid="notification-rate-limit-banner"
            >
              <span className="font-semibold">Email alerts:</span>
              {Object.entries(rateLimit).map(([sev, b]) => (
                <span
                  key={sev}
                  className={`px-2 py-0.5 rounded border ${
                    b.throttled
                      ? 'bg-red-100 border-red-300 text-red-800'
                      : b.remaining <= 2
                      ? 'bg-amber-100 border-amber-300 text-amber-800'
                      : 'bg-white border-gray-200 text-gray-700'
                  }`}
                  data-testid={`notification-rate-limit-${sev}`}
                >
                  {sev}: {b.throttled
                    ? `capped (${b.used}/${b.cap})`
                    : `${b.remaining} left this hour`}
                </span>
              ))}
              <span className="text-[10px] text-amber-700">
                (In-app alerts always show. Inbox pings resume next hour.)
              </span>
            </div>
          )}
          <div className="overflow-y-auto flex-1">
            {loading ? (
              <div className="p-6 text-center text-sm text-gray-400">Loading…</div>
            ) : items.length === 0 ? (
              <div className="p-8 text-center" data-testid="notification-empty">
                <div className="text-4xl mb-2">🔔</div>
                <p className="text-sm text-gray-500">You&apos;re all caught up.</p>
              </div>
            ) : (
              items.map((n) => {
                const isUnread = !(n.read_by || []).includes('self') && !n.resolved_at;
                const sev = SEVERITY_STYLES[n.severity] || SEVERITY_STYLES.P2;
                return (
                  <button
                    key={n.id}
                    onClick={() => goDeepLink(n)}
                    className={`w-full text-left px-4 py-3 border-b hover:bg-gray-50 transition-colors ${isUnread ? 'bg-cyan-50/50' : ''}`}
                    data-testid={`notification-row-${n.id}`}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded border ${sev}`}>
                        {n.severity}
                      </span>
                      {isUnread && <span className="w-2 h-2 rounded-full bg-cyan-500 flex-shrink-0 mt-1.5" />}
                    </div>
                    <p className={`text-sm mt-1 ${isUnread ? 'font-semibold text-gray-900' : 'text-gray-700'}`}>
                      {n.title}
                    </p>
                    <p className="text-xs text-gray-500 mt-1 line-clamp-2">{(n.body_md || '').slice(0, 200)}</p>
                    <div className="flex items-center justify-between mt-2 text-[10px] text-gray-400">
                      <span>{new Date(n.created_at).toLocaleString([], { dateStyle: 'short', timeStyle: 'short' })}</span>
                      {n.fire_count > 1 && <span>fired {n.fire_count}×</span>}
                      {n.resolved_at && <span className="text-green-600">✓ resolved</span>}
                    </div>
                  </button>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
